import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { TenantWidgetAccessUpdateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { TenantCreate } from "../../../tenant/components/create/TenantCreate";
import { TenantCreateStore } from "../../../tenant/store";
import { InputBoolean } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type TenantWidgetAccessUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  tenantSelectedValue?: { displayId: string; id: string } | null;
  onTenantChange?: (value: string) => void;
  onTenantSelected?: (value: { displayId: string; id: string } | null) => void;
  tenantOptions?: { displayId: string; id: string }[];
  tenantError?: string;
  widgetSelectedValue?: { displayId: string; id: string } | null;
  onWidgetChange?: (value: string) => void;
  onWidgetSelected?: (value: { displayId: string; id: string } | null) => void;
  widgetOptions?: { displayId: string; id: string }[];
  widgetError?: string;
  enabled: boolean;
  enabledSelectedValue?: { displayId: string; id: string } | null;
  onEnabledChange?: (value: boolean) => void;
  enabledError?: string;
  configOverride: string;
  configOverrideSelectedValue?: { displayId: string; id: string } | null;
  onConfigOverrideChange?: (value: string) => void;
  configOverrideError?: string;
  tenantWidgetAccessUpdateStore: TenantWidgetAccessUpdateStore;
};

export const TenantWidgetAccessUpdateForm: React.FC<TenantWidgetAccessUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onTenantSelected = () => {},
      tenantOptions = [],
      tenantSelectedValue = null,
      tenantError,
      onTenantChange = () => {},
      onWidgetSelected = () => {},
      widgetOptions = [],
      widgetSelectedValue = null,
      widgetError,
      onWidgetChange = () => {},
      enabled = false,
      enabledError,
      onEnabledChange = () => {},
      configOverride = "{}",
      configOverrideError,
      onConfigOverrideChange = () => {},
      tenantWidgetAccessUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="TenantWidgetAccess"
              mode="update"
              values={{}}
              options={{ tenant: tenantOptions, widget: widgetOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "tenant":
                    onTenantSelected?.(valor);
                    break;
                  case "widget":
                    onWidgetSelected?.(valor);
                    break;
                  case "enabled":
                    onEnabledChange?.(valor);
                    break;
                  case "configOverride":
                    onConfigOverrideChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={tenantWidgetAccessUpdateStore.getCardErrors("null")}
              title={langStore.groupTenantWidgetAccessLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="tenant"
                  label={langStore.TenantWidgetAccessTenantLabel}
                  autoFocus
                  error={Boolean(tenantError)}
                  helperText={
                    tenantError ||
                    tenantWidgetAccessUpdateStore.warnings["tenant"]?.message
                  }
                  selectedValue={tenantSelectedValue}
                  onChange={(value: string) => onTenantChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onTenantSelected(value)
                  }
                  options={tenantOptions}
                  description={langStore.TenantWidgetAccessTenantDescription}
                  canCreate={rootStore.authStore.hasPermissions(
                    "tenant_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new TenantCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <TenantCreate
                          tenantCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewTenant"
                      cancelMethod="cancelNewTenant"
                    />
                  )}
                />
                <InputReference
                  id="widget"
                  label={langStore.TenantWidgetAccessWidgetLabel}
                  error={Boolean(widgetError)}
                  helperText={
                    widgetError ||
                    tenantWidgetAccessUpdateStore.warnings["widget"]?.message
                  }
                  selectedValue={widgetSelectedValue}
                  onChange={(value: string) => onWidgetChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onWidgetSelected(value)
                  }
                  options={widgetOptions}
                  description={langStore.TenantWidgetAccessWidgetDescription}
                  canCreate={false}
                />
                <InputBoolean
                  value={enabled}
                  error={Boolean(enabledError)}
                  helperText={
                    enabledError ||
                    tenantWidgetAccessUpdateStore.warnings["enabled"]?.message
                  }
                  label={langStore.TenantWidgetAccessEnabledLabel}
                  description={langStore.TenantWidgetAccessEnabledDescription}
                  onChange={(value: boolean) => onEnabledChange(value)}
                />
                <InputCodeEditor
                  value={configOverride}
                  error={Boolean(configOverrideError)}
                  helperText={
                    configOverrideError ||
                    tenantWidgetAccessUpdateStore.warnings["configOverride"]
                      ?.message
                  }
                  label={langStore.TenantWidgetAccessConfigOverrideLabel}
                  onChange={(value: string) => onConfigOverrideChange(value)}
                  language="json"
                  description={
                    langStore.TenantWidgetAccessConfigOverrideDescription
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
